import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Gift, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Redeem() {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim() || !code.trim()) {
      toast.error("Please enter your phone number and the code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/redeem`, {
        phone: phone.trim(),
        code: code.trim().toUpperCase(),
      });
      setResult(data);
      toast.success(`${data.pack_size} tokens redeemed.`);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your code and phone number.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2EFE7] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <a
          href="/"
          className="inline-flex items-center gap-2 text-sm font-bold text-[#2D4A22] hover:underline mb-8"
          data-testid="redeem-home-link"
        >
          ← Back to arvo
        </a>

        <div className="bg-white border border-[#D5D1C8] rounded-md shadow-sm p-8">
          {result ? (
            <div data-testid="redeem-success" className="text-center">
              <CheckCircle2 className="w-14 h-14 text-[#2D4A22] mx-auto" strokeWidth={2.2} />
              <h1 className="mt-5 font-display font-bold text-3xl text-[#1A1D1A]">
                {result.pack_size} tokens claimed
              </h1>
              <p className="mt-4 text-[#4A4F4A] leading-relaxed">
                Your tokens will be added to your Arvo account within a few
                minutes. Open the Arvo app and you&apos;re good to go.
              </p>
              <p className="mt-5 text-xs font-mono text-[#4A4F4A] bg-[#F9F8F6] border border-[#D5D1C8] rounded-md py-2">
                Code: <strong>{result.code}</strong>
              </p>
              <a
                href="/"
                data-testid="redeem-success-home"
                className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#D96C32] hover:text-[#B85A28]"
              >
                Back to arvo <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-[#D96C32] mb-4">
                <Gift className="w-5 h-5" strokeWidth={2.2} />
                <span className="text-xs font-bold tracking-[0.18em] uppercase">
                  Redeem your code
                </span>
              </div>

              <h1
                data-testid="redeem-headline"
                className="font-display font-bold text-3xl sm:text-4xl text-[#1A1D1A] leading-tight"
              >
                Top up your Arvo tokens.
              </h1>
              <p className="mt-3 text-[#4A4F4A] leading-relaxed">
                Got a code from the Arvo team? Enter it below with the mobile
                number on your Arvo account and we&apos;ll add the tokens.
              </p>

              <form onSubmit={submit} className="mt-7 space-y-4">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2"
                  >
                    Mobile number
                  </label>
                  <input
                    id="phone"
                    data-testid="redeem-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="021 123 4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full border border-[#D5D1C8] rounded-md px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#2D4A22] focus:border-transparent"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label
                    htmlFor="code"
                    className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2"
                  >
                    Redeem code
                  </label>
                  <input
                    id="code"
                    data-testid="redeem-code"
                    type="text"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    placeholder="ARVO-XXXX-XXXX"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    className="w-full border border-[#D5D1C8] rounded-md px-4 py-3 text-base font-mono tracking-wider uppercase focus:outline-none focus:ring-2 focus:ring-[#2D4A22] focus:border-transparent"
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="redeem-submit"
                  disabled={loading}
                  className="w-full bg-[#2D4A22] hover:bg-[#1F3A18] text-white font-bold py-4 rounded-md shadow-sm hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {loading ? (
                    <span className="inline-flex items-center justify-center gap-2">
                      <Loader2 className="w-5 h-5 animate-spin" /> Checking…
                    </span>
                  ) : (
                    <span className="inline-flex items-center justify-center gap-2">
                      Redeem code <ArrowRight className="w-5 h-5" />
                    </span>
                  )}
                </button>
              </form>

              <p className="mt-5 text-xs text-[#4A4F4A]/80 leading-relaxed">
                Use the same number you signed up with in the Arvo app. Codes
                can only be redeemed once.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
